import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { FileText, Save, Printer, ArrowLeft, CheckCircle, XCircle, Minus } from 'lucide-react';
import api from '../api';

const QAPIPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    agency: '',
    qmYr: '',
    soc: '',
    certPeriod: '',
    certTo: '',
    status: 'Active',
    dischargeDate: '',
    patientName: '',
    payer: '',
    dx: '',
    
    // Section responses - each item has: value ('yes', 'no', 'na'), comment
    admissionForms: {},
    psychosocialIssues: {},
    medications: {},
    nursingDocumentation: {},
    physicalTherapy: {},
    occupationalTherapy: {},
    speechTherapy: {}
  });

  // Section definitions
  const sections = {
    admissionForms: {
      title: 'I. ADMISSION FORMS',
      items: [
        { id: 'referral_48hrs', text: 'Referral admitted w/in 48 hour or physician ordered SOC', cop: '484.55' },
        { id: 'assessment_5days', text: 'Assessment complete no later than 5 days after SOC date', cop: '484.55' },
        { id: 'rn_conducts', text: 'RN conducts comprehensive assessment and determines eligibility', cop: '484.55' },
        { id: 'hha_conducts', text: 'HHA conducts comprehensive assessment / Related OASIS', cop: '' },
        { id: 'only_service', text: 'Only service ordered', cop: '' },
        { id: 'discipline_frequency', text: 'Discipline frequency identified in consent: SN/OT, POC', cop: '484.18' },
        { id: 'advance_directives', text: 'Advance Directives - HHA/s, DNR, Dorn', cop: '' },
        { id: 'patient_informed', text: 'Patient informed of rights/informed/concerns, etc', cop: '484.10' },
        { id: 'written_notice', text: 'Written notice of rights, documentation that patient received', cop: '484.10' },
        { id: 'consent_form', text: 'Consent form completed is signed/dated', cop: '484.10' },
        { id: 'patient_unable', text: 'Patient PI unable to sign consent noted', cop: '484.10' },
        { id: 'patient_agency', text: 'Patient agency for payment plus and written notice', cop: '484.10' },
        { id: 'notice_written', text: 'Notice or letter of financial liability if needed within 22 days', cop: '484.10' },
        { id: 'evidence_patient', text: 'Evidence that patient participates in Plan of care', cop: '484.10' },
      ]
    },
    psychosocialIssues: {
      title: 'II. PSYCHOSOCIAL ISSUES',
      items: [
        { id: 'poc_complete', text: 'POC Complete', cop: '484.18, 484.55' },
        { id: 'evidence_patient_needs', text: 'Evidence that patient needs addressed: Plan if applicable', cop: '484.10' },
        { id: 'face_to_face', text: 'Face To Face encounter meets regulations', cop: '' },
        { id: 'supporting_f2f', text: 'Supporting F2F documents signed/dated by physician', cop: '' },
        { id: 'recertification', text: 'Recertification and initial documentation required only', cop: '' },
        { id: 'all_orders_signed', text: 'All orders signed & dated by physician prior to EOE or', cop: '484.18' },
        { id: 'comprehensive_updated', text: 'Comprehensive assessment updated as needed: SOC/ROC', cop: '484.55, 484.18' },
        { id: 'comprehensive_revised', text: 'Comprehensive assessment revised within 48 hrs of patient return home following inpatient admission for reasons other', cop: '484.55, 484.18' },
        { id: 'snc_specialty', text: 'SNC specialty Info', cop: '' },
        { id: 'verbal_order', text: 'Verbal order for services/assessments: dated 23 of from 485 signed and dated by RN /Related as applicable', cop: '484.18' },
        { id: 'verbal_orders_present', text: 'Verbal orders present for change in Hgb -+', cop: '484.18' },
        { id: 'advance_notice', text: 'Advance notice to patient of care and charges is POC', cop: '484.10' },
        { id: 'oasis_certified', text: 'Oasis certified for each epi/ode', cop: '484.10, 484.55, 484.18' },
        { id: 'oasis_md_date', text: 'Oasis reflect and date for disc 80/181 date as applicable', cop: '' },
        { id: 'oasis_timeframe', text: 'Oasis submitted meet/meets 7 day/note', cop: '484.18' },
        { id: '60_day_summary', text: '60 Day Summary complete meets only', cop: '484.18, 484.55' },
      ]
    },
    medications: {
      title: 'III. MEDICATIONS',
      items: [
        { id: 'information_drug', text: 'Information on Drug Regimen review: complete', cop: '484.55' },
        { id: 'issues_identified', text: 'Issues identified and intervention documented', cop: '484.55' },
        { id: 'reviewed_clinically', text: 'Reviewed for clinically significant medication issues', cop: '484.55' },
        { id: 'intervention_non', text: 'Intervention for non-compliance documented', cop: '484.55' },
        { id: 'updated_needed', text: 'Updated as needed and in 60 days', cop: '484.55, 484.55' },
        { id: 'documented_ordered', text: 'Documented ordered drug/treatments ordered by physician', cop: '484.18' },
        { id: 'high_risk_meds', text: 'High Risk meds noted, teaching intervention/monitoring documented', cop: '' },
        { id: 'oasis_m2010', text: 'OASIS M2010', cop: '' },
      ]
    },
    nursingDocumentation: {
      title: 'IV. NURSING DOCUMENTATION',
      items: [
        { id: 'sn_frequency', text: 'SN frequency per orders', cop: '484.10, 484.18' },
        { id: 'documentation_per', text: 'Documentation per orders', cop: '484.55' },
        { id: 'poc_followed', text: 'POC followed – orders match care given', cop: '484.18, 484.55' },
        { id: 'frequency_matches', text: 'Frequency matches documented', cop: '484.18' },
        { id: 'evidence_care', text: 'Evidence care is clinical based established goals', cop: '484.55' },
        { id: 'med_list_matches', text: 'Med list of care duration appropriate for pt needs', cop: '484.18, 484.55' },
        { id: 'documentation_present', text: 'Documentation present for missed visit, physician notified', cop: '484.18' },
        { id: 'homebound_status', text: 'Homebound status assessed: confirmed', cop: '484.55' },
        { id: 'interdisciplinary_care', text: 'Interdisciplinary care coordination documented', cop: '484.18' },
        { id: 'lab_results', text: 'Lab results reported to physician and documented', cop: '484.18, 484.55' },
        { id: 'found_care_infection', text: 'Found care per protocol / Medication/Insulin noted', cop: '484.55' },
        { id: 'evidence_rn', text: 'Evidence RN makes POC and reviews', cop: '484.55' },
        { id: 'evidence_rn_regularly', text: 'Evidence RN regularly scheduled patient nursing needs', cop: '484.55' },
        { id: 'cosign_rn', text: 'COSIGN reports to RN for problems - plan of care changes', cop: '484.55' },
        { id: 'communication_physician', text: 'Communication with physician re: change documented', cop: '484.10, 484.18' },
      ]
    },
    physicalTherapy: {
      title: 'V. PHYSICAL THERAPY',
      items: [
        { id: 'pt_orders_poc', text: 'Orders on POC verify orders', cop: '484.18' },
        { id: 'pt_evaluation_complete', text: 'Evaluation complete w/in 3 days of referral', cop: '' },
        { id: 'pt_assess_physician', text: 'PT assess physician in eval and on F/U with – POC', cop: '484.55' },
        { id: 'pt_evaluation_includes', text: 'Evaluation includes ordered assessments/standardized tests', cop: '' },
        { id: 'pt_evaluation_identifies', text: 'Evaluation identifies deficits, resulting skilled intervention', cop: '484.55' },
        { id: 'pt_goals_functional', text: 'Goals are functional and measurable', cop: '484.55' },
        { id: 'pt_documentation_shows', text: 'Documentation shows progress toward goals', cop: '484.55' },
        { id: 'pt_documentation_drug', text: 'Documentation shows drug education', cop: '484.55' },
        { id: 'pt_frequency_per', text: 'PT frequency per orders', cop: '484.18' },
        { id: 'pt_evidence_care', text: 'Evidence care is clinical based established goals', cop: '484.55' },
        { id: 'pt_notes_reflect', text: 'Notes reflect skilled care provided', cop: '484.55' },
        { id: 'pt_functional_reassess', text: 'Functional Reassessment at least every 30 days', cop: '' },
        { id: 'pt_cosign_supervised', text: 'COTA supervised by OT', cop: '484.55' },
        { id: 'pt_pta_reports', text: 'PTA reports to PT for plan of care changes', cop: '484.55' },
        { id: 'pt_discharge_summary', text: 'Discharge summary: demonstrates achievement of goals', cop: '' },
        { id: 'pt_documentation_missed', text: 'Documentation present for missed visit', cop: '484.18' },
        { id: 'pt_poc_intervention', text: 'POC intervention: Updated Due to Missed visits', cop: '' },
        { id: 'pt_per_assessment', text: 'Per assessment, intervention documented', cop: '' },
        { id: 'pt_homebound_status', text: 'Homebound status assessed: confirmed', cop: '484.55' },
        { id: 'pt_interdisciplinary', text: 'Interdisciplinary care coordination documented', cop: '484.18' },
        { id: 'pt_communication', text: 'Communication with physician re: change documented', cop: '484.55' },
      ]
    },
    occupationalTherapy: {
      title: 'VI. OCCUPATIONAL THERAPY',
      items: [
        { id: 'ot_orders_adn', text: 'Orders on ADN verify orders', cop: '484.18' },
        { id: 'ot_evaluation_complete', text: 'Evaluation complete w/in 3 days of referral', cop: '' },
        { id: 'ot_assess_physician', text: 'OT assess physician in eval and on F/U with – POC', cop: '484.55' },
        { id: 'ot_evaluation_includes', text: 'Evaluation includes ordered assessments/standardized tests', cop: '' },
        { id: 'ot_evaluation_identifies', text: 'Evaluation identifies deficits, resulting skilled intervention', cop: '484.55' },
        { id: 'ot_goals_functional', text: 'Goals are functional and measurable', cop: '484.55' },
        { id: 'ot_documentation_shows', text: 'Documentation shows progress toward goals', cop: '484.55' },
        { id: 'ot_documentation_drug', text: 'Documentation shows drug education', cop: '484.55' },
        { id: 'ot_frequency_per', text: 'OT frequency per orders', cop: '484.18' },
        { id: 'ot_evidence_care', text: 'Evidence care is clinical based established goals', cop: '484.55' },
        { id: 'ot_notes_reflect', text: 'Notes reflect skilled care provided', cop: '484.55' },
        { id: 'ot_functional_reassess', text: 'Functional Reassessment at least every 30 days', cop: '' },
        { id: 'ot_cota_supervised', text: 'COTA supervised by OT', cop: '484.55' },
        { id: 'ot_cota_reports', text: 'COTA reports to OT for plan of care changes', cop: '484.55' },
        { id: 'ot_discharge_summary', text: 'Discharge summary: demonstrates achievement of goals', cop: '' },
        { id: 'ot_documentation_missed', text: 'Documentation present for missed visit', cop: '484.18' },
        { id: 'ot_poc_intervention', text: 'POC intervention: Updated Due to Missed visits', cop: '' },
        { id: 'ot_per_assessment', text: 'Per assessment, intervention documented', cop: '' },
        { id: 'ot_homebound_status', text: 'Homebound status assessed: confirmed', cop: '484.55' },
        { id: 'ot_interdisciplinary', text: 'Interdisciplinary care coordination documented', cop: '484.18' },
        { id: 'ot_communication', text: 'Communication with physician re: change documented', cop: '484.55' },
      ]
    },
    speechTherapy: {
      title: 'V. SPEECH THERAPY',
      items: [
        { id: 'st_orders_poc', text: 'Orders on POC verify orders', cop: '484.18' },
        { id: 'st_evaluation_complete', text: 'Evaluation complete w/in 3 days of referral', cop: '' },
        { id: 'st_assess_physician', text: 'SLP assess physician in eval and on F/U with – POC', cop: '484.55' },
        { id: 'st_evaluation_includes', text: 'Evaluation includes ordered assessments/standardized tests', cop: '' },
        { id: 'st_evaluation_identifies', text: 'Evaluation identifies deficits, resulting skilled intervention', cop: '484.55' },
        { id: 'st_goals_functional', text: 'Goals are functional and measurable', cop: '484.55' },
        { id: 'st_documentation_shows', text: 'Documentation shows progress toward goals', cop: '484.55' },
        { id: 'st_documentation_drug', text: 'Documentation shows drug education', cop: '484.55' },
        { id: 'st_frequency_per', text: 'SLP frequency per orders', cop: '484.18' },
        { id: 'st_evidence_care', text: 'Evidence care is clinical based established goals', cop: '484.55' },
        { id: 'st_notes_reflect', text: 'Notes reflect skilled care provided', cop: '484.55' },
        { id: 'st_functional_reassess', text: 'Functional Reassessment at least every 30 days', cop: '' },
        { id: 'st_discharge_summary', text: 'Discharge summary: demonstrates achievement of goals', cop: '' },
        { id: 'st_documentation_missed', text: 'Documentation present for missed visit', cop: '484.18' },
        { id: 'st_poc_intervention', text: 'POC intervention: Updated Due to Missed visits', cop: '' },
        { id: 'st_per_assessment', text: 'Per assessment, intervention documented', cop: '' },
        { id: 'st_homebound_status', text: 'Homebound status assessed: confirmed', cop: '484.55' },
        { id: 'st_interdisciplinary', text: 'Interdisciplinary care coordination documented', cop: '484.18' },
        { id: 'st_communication', text: 'Communication with physician re: change documented', cop: '484.55' },
      ]
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (section, itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [itemId]: {
          ...prev[section]?.[itemId],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.post('/qapi/reviews', formData);
      if (response.data.success) {
        toast.success('QAPI Review saved successfully');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save QAPI Review');
    } finally {
      setSaving(false);
    }
  };

  const RadioGroup = ({ section, itemId }) => {
    const value = formData[section]?.[itemId]?.value || '';
    return (
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => handleItemChange(section, itemId, 'value', 'yes')}
          className={`w-8 h-6 flex items-center justify-center rounded text-xs font-medium transition-colors ${
            value === 'yes' ? 'bg-green-500 text-white' : 'bg-gray-100 hover:bg-green-100'
          }`}
        >
          Y
        </button>
        <button
          type="button"
          onClick={() => handleItemChange(section, itemId, 'value', 'no')}
          className={`w-8 h-6 flex items-center justify-center rounded text-xs font-medium transition-colors ${
            value === 'no' ? 'bg-red-500 text-white' : 'bg-gray-100 hover:bg-red-100'
          }`}
        >
          N
        </button>
        <button
          type="button"
          onClick={() => handleItemChange(section, itemId, 'value', 'na')}
          className={`w-8 h-6 flex items-center justify-center rounded text-xs font-medium transition-colors ${
            value === 'na' ? 'bg-gray-500 text-white' : 'bg-gray-100 hover:bg-gray-200'
          }`}
        >
          NA
        </button>
      </div>
    );
  };

  const renderSection = (sectionKey, sectionData) => (
    <div key={sectionKey} className="mb-6">
      <div className="bg-gray-800 text-white px-3 py-2 text-sm font-bold">
        {sectionData.title}
      </div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-1 text-left w-1/2">Item</th>
            <th className="border px-2 py-1 text-center w-16">COP</th>
            <th className="border px-2 py-1 text-center w-24">YES/NO/NA</th>
            <th className="border px-2 py-1 text-left">COMMENTS</th>
          </tr>
        </thead>
        <tbody>
          {sectionData.items.map((item, index) => (
            <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="border px-2 py-1">{item.text}</td>
              <td className="border px-2 py-1 text-center text-gray-600">{item.cop}</td>
              <td className="border px-2 py-1">
                <RadioGroup section={sectionKey} itemId={item.id} />
              </td>
              <td className="border px-1 py-1">
                <input
                  type="text"
                  value={formData[sectionKey]?.[item.id]?.comment || ''}
                  onChange={(e) => handleItemChange(sectionKey, item.id, 'comment', e.target.value)}
                  className="w-full px-1 py-0.5 text-xs border-0 focus:ring-1 focus:ring-blue-300"
                  placeholder="Add comment..."
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-4 bg-gray-100 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b mb-4 p-3 flex justify-between items-center rounded shadow-sm">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800">Quality Assessment Performance Improvement</h1>
              <p className="text-xs text-gray-500">Clinical Record Review</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#3498db] hover:bg-[#2980b9]">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Form Container */}
        <div className="bg-white border rounded shadow-sm max-w-7xl mx-auto">
          {/* Title */}
          <div className="border-b p-3 text-center">
            <h2 className="text-lg font-bold">CLINICAL RECORD REVIEW</h2>
          </div>

          {/* Patient Info Header */}
          <div className="border-b p-3 grid grid-cols-6 gap-3 text-sm">
            <div className="col-span-1">
              <label className="text-xs text-gray-600">Agency:</label>
              <Input
                value={formData.agency}
                onChange={(e) => handleInputChange('agency', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Qm/Yr:</label>
              <Input
                value={formData.qmYr}
                onChange={(e) => handleInputChange('qmYr', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">SOC:</label>
              <Input
                type="date"
                value={formData.soc}
                onChange={(e) => handleInputChange('soc', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Cert Period:</label>
              <Input
                value={formData.certPeriod}
                onChange={(e) => handleInputChange('certPeriod', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">To:</label>
              <Input
                type="date"
                value={formData.certTo}
                onChange={(e) => handleInputChange('certTo', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === 'Active'}
                  onChange={() => handleInputChange('status', 'Active')}
                />
                Active
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === 'Discharged'}
                  onChange={() => handleInputChange('status', 'Discharged')}
                />
                Discharged
              </label>
            </div>
          </div>

          <div className="border-b p-3 grid grid-cols-5 gap-3 text-sm">
            <div className="col-span-2">
              <label className="text-xs text-gray-600">Patient Name:</label>
              <Input
                value={formData.patientName}
                onChange={(e) => handleInputChange('patientName', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Payer:</label>
              <select
                value={formData.payer}
                onChange={(e) => handleInputChange('payer', e.target.value)}
                className="w-full h-7 text-sm border rounded px-2"
              >
                <option value="">Select...</option>
                <option value="MCR">MCR</option>
                <option value="MCD">MCD</option>
                <option value="HMO">HMO</option>
                <option value="VA">VA</option>
                <option value="Private Ins">Private Ins</option>
                <option value="S-YNRF">S-YNRF</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-600">Dx:</label>
              <Input
                value={formData.dx}
                onChange={(e) => handleInputChange('dx', e.target.value)}
                className="h-7 text-sm"
              />
            </div>
          </div>

          {/* Sections */}
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              {renderSection('admissionForms', sections.admissionForms)}
              {renderSection('psychosocialIssues', sections.psychosocialIssues)}
              {renderSection('medications', sections.medications)}
            </div>
            <div>
              {renderSection('nursingDocumentation', sections.nursingDocumentation)}
              {renderSection('physicalTherapy', sections.physicalTherapy)}
              {renderSection('occupationalTherapy', sections.occupationalTherapy)}
              {renderSection('speechTherapy', sections.speechTherapy)}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default QAPIPage;
